import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { DismissOption, ViewType } from '../../../constants/parameters';
import {
  mapEmailErrorToResource,
  mapPasswordResetErrorToResource
} from '../../../constants/resources';
import { ModalFragmentProps } from '../../../constants/types';
import useAccountSecurityPromptContext from '../../../hooks/useAccountSecurityPromptContext';
import { AccountSecurityPromptActionType } from '../../../store/action';
import ModalState from '../../../store/modalState';
import { dismissTemporary, startDismissForever } from '../../commonHandlers';
import InputControl, { validateEmailAddress } from '../fragments/inputControl';
import { FooterButtonConfig, FragmentModalFooter } from '../fragments/modalFooter';
import { FragmentModalHeader, HeaderButtonType } from '../fragments/modalHeader';

/**
 * The start of the add email modal flow.
 */
const ModalAddEmailForm: React.FC<ModalFragmentProps> = ({ closeModal }: ModalFragmentProps) => {
  const {
    state: { promptType, viewType, dismissOption, resources, requestService, emailAddress },
    dispatch
  } = useAccountSecurityPromptContext();
  const setEmailAddress = (value: string) =>
    dispatch({
      type: AccountSecurityPromptActionType.SET_EMAIL_ADDRESS,
      emailAddress: value
    });

  /*
   * Component State
   */

  const [emailAddressError, setEmailAddressError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestInFlight, setRequestInFlight] = useState<boolean>(false);

  /*
   * Event Handlers
   */

  const clearRequestError = () => setRequestError(null);

  const addEmail = async () => {
    setRequestError(null);
    setRequestInFlight(true);
    // First, add the email address to the user's account (unverified).
    const updateResult = await requestService.updateEmail(emailAddress);
    if (updateResult.isError) {
      setRequestInFlight(false);
      setRequestError(mapEmailErrorToResource(resources, updateResult.error));
      return;
    }

    // Second, send the user a reset password email.
    const resetResult = await requestService.resetSendPrompted();
    setRequestInFlight(false);
    if (resetResult.isError) {
      setRequestError(mapPasswordResetErrorToResource(resources, resetResult.error));
    } else {
      // Transition: Email successfully added.
      dispatch({
        type: AccountSecurityPromptActionType.SET_MODAL_STATE,
        modalState: ModalState.ADD_EMAIL_CONFIRMATION
      });
    }
  };

  /*
   * Render Properties
   */

  const inputValid = emailAddress !== '' && emailAddressError === null;

  const positiveButton: FooterButtonConfig = {
    // Show a spinner as the button content when a request is in flight.
    content: requestInFlight ? (
      <span className='spinner spinner-xs spinner-no-margin' />
    ) : (
      resources.Action.ContinueAddEmail
    ),
    label: resources.Action.ContinueAddEmail,
    enabled: !requestInFlight && inputValid,
    action: addEmail
  };

  const negativeButton: FooterButtonConfig | null =
    viewType === ViewType.BANNER || dismissOption === DismissOption.TEMPORARY_ONLY
      ? null
      : {
          content: resources.Action.DismissForever,
          label: resources.Action.DismissForever,
          enabled: !requestInFlight,
          action: startDismissForever(dispatch, promptType)
        };

  /*
   * Component Markup
   */

  return (
    <React.Fragment>
      <FragmentModalHeader
        headerText={resources.Header.AddYourEmail}
        buttonType={HeaderButtonType.CLOSE}
        buttonAction={viewType === ViewType.BANNER ? closeModal : dismissTemporary(dispatch)}
        buttonEnabled={!requestInFlight}
        headerInfo={null}
      />
      <Modal.Body>
        <div className='modal-shield' />
        <p className='modal-margin-bottom modal-text-danger'>
          {resources.Description.UnusualActivity}
        </p>
        <p className='modal-margin-bottom-xlarge'>{resources.Description.AddYourEmail}</p>
        <InputControl
          id='inputEmail'
          label={resources.Label.YourEmail}
          inputType='email'
          placeholder='user@example.com'
          disabled={false}
          value={emailAddress}
          setValue={setEmailAddress}
          error={emailAddressError || requestError}
          setError={setEmailAddressError}
          validate={validateEmailAddress(resources.Message.Error.Input.InvalidEmail)}
          handleSubmit={addEmail}
          onChange={clearRequestError}
        />
      </Modal.Body>
      <FragmentModalFooter positiveButton={positiveButton} negativeButton={negativeButton} />
    </React.Fragment>
  );
};

export default ModalAddEmailForm;
